import { onlineManager } from '@tanstack/react-query';
import type { StreamChange } from 'sdk';
import type { ProductEntityType } from 'shared';
import { invalidateUnseenCounts } from '~/modules/seen/query';
import { getEntityQueryKeys, hasEntityQueryKeys } from '~/query/basic/entity-query-registry';
import { sourceId } from '~/query/offline/hlc';
import { queryClient } from '~/query/query-client';
import * as cacheOps from '~/query/realtime/cache-ops';
import { propagateEmbeddings } from '~/query/realtime/propagation';
import { publishSyncedRows } from '~/query/realtime/sync-events';
import { getSyncTier, isViewingChannel, type SyncTier, tierBackground } from '~/query/realtime/sync-priority';
import { syncStore } from '~/query/realtime/sync-store';
import { resolveChannelPath } from '~/query/realtime/view-declaration';

/** Spread window of a range that carries none from the server, such as a catchup gap. */
const defaultSpreadWindowMs = 15_000;
/** Transient fetch errors retry with exponential backoff before falling back to invalidation. */
const maxFlushAttempts = 3;
const retryBaseMs = 2_000;
/** Re-check delay while offline, where a fetch would fail; catchup reconciles on reconnect. */
const offlineRecheckMs = 5_000;

export interface EnqueueInput {
  entityType: ProductEntityType;
  organizationId: string;
  tenantId: string | null;
  channelId: string | null;
  afterSeq: number;
  untilSeq: number;
  isCreate: boolean;
  spreadWindowMs?: number;
  propagation?: StreamChange['propagation'];
}

interface DirtyEntry {
  entityType: ProductEntityType;
  organizationId: string;
  tenantId: string | null;
  channelId: string | null;
  afterSeq: number;
  untilSeq: number;
  dueAt: number;
  hasCreates: boolean;
  attempts: number;
  propagations: NonNullable<StreamChange['propagation']>[];
}

const dirty = new Map<string, DirtyEntry>();
let timer: ReturnType<typeof setTimeout> | null = null;
let listenersInstalled = false;

const entryKey = (entityType: ProductEntityType, organizationId: string, channelId: string | null) => `${entityType}:${channelId ?? organizationId}`;

/** FNV-1a 32-bit gives the same client and scope a stable spread slot. */
function hashSpread(key: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** The negotiated delay: client tier bounds the server-spread slot. */
function negotiatedDelay(tier: SyncTier, channelViewKey: string, windowMs: number): number {
  const spread = windowMs > 0 ? hashSpread(`${sourceId}:${channelViewKey}`) % windowMs : 0;
  return Math.min(Math.max(tier.min, spread), tier.max);
}

/** Enqueue an announced seq range for lazy fetching; a muted or archived scope waits until it is opened. */
export function enqueueRange(input: EnqueueInput): void {
  enqueueWithTier(input, getSyncTier(input.organizationId, input.channelId));
}

/** Enqueue catchup gaps, including muted views in spread background work so persisted caches converge. */
export function enqueueCatchupRange(input: EnqueueInput): void {
  const tier = getSyncTier(input.organizationId, input.channelId);
  enqueueWithTier(input, tier.min === Number.POSITIVE_INFINITY ? tierBackground : tier);
}

function enqueueWithTier(input: EnqueueInput, tier: SyncTier): void {
  const { entityType, organizationId, tenantId, channelId, afterSeq, untilSeq, isCreate, propagation } = input;
  if (!hasEntityQueryKeys(entityType)) return;

  const store = syncStore.getState();
  const caughtUp = channelId ? store.getChannelSeq(organizationId, channelId, entityType) : store.getOrgSeq(organizationId, entityType);
  if (untilSeq <= caughtUp) return;

  // Anchoring after the caught-up position heals missed-change gaps and trims already-ingested overlap; with no baseline, trust the change's own range.
  const anchoredAfter = caughtUp > 0 ? caughtUp : afterSeq;

  const key = entryKey(entityType, organizationId, channelId);
  // The on-open tier gives an infinite delay: the entry waits for promoteLiveChannels.
  const dueAt = Date.now() + negotiatedDelay(tier, key, input.spreadWindowMs ?? defaultSpreadWindowMs);
  const entry: DirtyEntry = {
    entityType,
    organizationId,
    tenantId,
    channelId,
    afterSeq: anchoredAfter,
    untilSeq,
    dueAt,
    hasCreates: isCreate,
    attempts: 0,
    propagations: propagation ? [propagation] : [],
  };

  const existing = dirty.get(key);
  if (existing) {
    mergeRange(existing, entry);
    // More news never postpones.
    existing.dueAt = Math.min(existing.dueAt, dueAt);
  } else {
    dirty.set(key, entry);
  }

  installListeners();
  armTimer();
}

/** Attach a catchup propagation hint to the pending org range of the hint's embedded product, so it runs after the fetch that ingests the fresh embedded rows. False when nothing is pending for that scope; the caller then propagates at once. */
export function attachPendingPropagation(
  entityType: ProductEntityType,
  organizationId: string,
  propagation: NonNullable<StreamChange['propagation']>,
): boolean {
  const entry = dirty.get(entryKey(entityType, organizationId, null));
  if (!entry) return false;
  entry.propagations.push(propagation);
  return true;
}

/** Flush every dirty scope now except those waiting to be opened (tab hiding, tests). */
export function flushAllNow(): Promise<void> {
  for (const entry of dirty.values()) if (entry.dueAt !== Number.POSITIVE_INFINITY) entry.dueAt = 0;
  return flushDue();
}

/** Flush one foreground catchup view before mutation replay; a single failed attempt invalidates and advances. */
export async function flushChannelViewNow(
  entityType: ProductEntityType,
  organizationId: string,
  channelId: string | null,
): Promise<'ok' | 'fallback' | 'requeued' | 'none'> {
  const key = entryKey(entityType, organizationId, channelId);
  const entry = dirty.get(key);
  if (!entry) return 'none';
  dirty.delete(key);
  return flushGroup([{ ...entry, attempts: maxFlushAttempts - 1 }]);
}

/** Clear all pending work. Called when catchup starts: it recomputes channel-view gaps itself. */
export function resetFetchPrioritizer(): void {
  dirty.clear();
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}

function armTimer(): void {
  let earliest = Number.POSITIVE_INFINITY;
  for (const entry of dirty.values()) earliest = Math.min(earliest, entry.dueAt);
  // Nothing is due before an open: a timeout past 2^31-1 ms would fire at once.
  if (earliest === Number.POSITIVE_INFINITY) return;

  const delay = Math.max(0, earliest - Date.now());
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    void flushDue();
  }, delay);
}

async function flushDue(): Promise<void> {
  const now = Date.now();
  const due: DirtyEntry[] = [];
  for (const [key, entry] of dirty) {
    if (entry.dueAt > now) continue;
    // In-flight ranges leave the map; overlapping changes that arrive later re-enqueue.
    dirty.delete(key);
    due.push(entry);
  }
  // Covering fetch: all due channels of one (entityType, org) share one bounded fetch, and rows route to their home lists during patching.
  const groups = new Map<string, DirtyEntry[]>();
  for (const entry of due) {
    const groupKey = `${entry.entityType}:${entry.organizationId}`;
    const group = groups.get(groupKey);
    if (group) group.push(entry);
    else groups.set(groupKey, [entry]);
  }
  await Promise.all([...groups.values()].map(flushGroup));
  armTimer();
}

/** Common channel covering due views for one narrowed range fetch; org-level, unresolved, or root-divergent paths widen to the whole organization. */
function coveringChannelId(entries: DirtyEntry[]): string | undefined {
  const ids = new Set<string>();
  for (const entry of entries) {
    if (!entry.channelId || entry.channelId === entry.organizationId) return undefined;
    ids.add(entry.channelId);
  }
  if (ids.size === 1) return [...ids][0];

  const paths: string[][] = [];
  for (const id of ids) {
    const path = resolveChannelPath(id);
    if (!path) return undefined;
    paths.push(path.split('/'));
  }
  let common = paths[0];
  for (const segments of paths.slice(1)) {
    let i = 0;
    while (i < common.length && i < segments.length && common[i] === segments[i]) i++;
    common = common.slice(0, i);
  }
  // A single root segment is the org id, so no narrowing; otherwise the deepest common segment is the covering channel id.
  return common.length > 1 ? common[common.length - 1] : undefined;
}

/** Flush one covering group: fetch the merged range once, then settle every entry from it. */
async function flushGroup(entries: DirtyEntry[]): Promise<'ok' | 'fallback' | 'requeued'> {
  const { entityType, organizationId } = entries[0];
  const tenantId = entries.find((entry) => entry.tenantId)?.tenantId ?? null;

  if (!onlineManager.isOnline()) {
    for (const entry of entries) requeue(entry, offlineRecheckMs);
    return 'requeued';
  }

  const afterSeq = Math.min(...entries.map((entry) => entry.afterSeq));
  const untilSeq = Math.max(...entries.map((entry) => entry.untilSeq));
  const keys = getEntityQueryKeys(entityType);
  const channelId = coveringChannelId(entries);
  const result = await cacheOps.fetchRangeAndPatch(entityType, organizationId, tenantId, { afterSeq, untilSeq }, keys, channelId);

  if (result.status === 'error' && entries.some((entry) => entry.attempts + 1 < maxFlushAttempts)) {
    for (const entry of entries) requeue({ ...entry, attempts: entry.attempts + 1 }, retryBaseMs * 2 ** entry.attempts);
    return 'requeued';
  }

  // Each due channel advances to the shared upper bound, also past a range not delivered whole: its refetch mark recovers it, and no later catchup fetches it again.
  for (const entry of entries) advanceCaughtUp({ ...entry, untilSeq });

  // The org id is the fallback home for rows without ancestor ids.
  if (result.status === 'ok') publishSyncedRows({ entityType, organizationId, rows: result.items });

  // Overflow, unsupported, exhausted retries, or fewer rows than announced: a row changed again since, one this reader may not read, or one deleted with its channel.
  const undelivered = result.status !== 'ok' || result.reachedSeq < untilSeq;
  if (undelivered) {
    invalidateUndelivered(
      entityType,
      organizationId,
      entries.map((entry) => entry.channelId),
      entries.some((entry) => entry.hasCreates),
    );
  }

  // Propagate after source data settled (fresh rows on ok, invalidated list otherwise).
  for (const entry of entries) {
    for (const propagation of entry.propagations) propagateEmbeddings(propagation);
  }

  return undelivered ? 'fallback' : 'ok';
}

/**
 * Rows of a scope the client cannot fetch one by one: the refetch mark recovers its lists, at once on screen, else when
 * opened or read by the sync service, and `recountUnseen` asks for an exact unseen recount.
 */
export function invalidateUndelivered(
  entityType: ProductEntityType,
  organizationId: string,
  channelIds: (string | null)[],
  recountUnseen: boolean,
): void {
  const anyViewing = channelIds.some((channelId) => isViewingChannel(organizationId, channelId));
  cacheOps.invalidateEntityListForOrg(getEntityQueryKeys(entityType), organizationId, anyViewing ? 'active' : 'none');
  if (recountUnseen) invalidateUnseenCounts(entityType);
}

function advanceCaughtUp(entry: DirtyEntry): void {
  const store = syncStore.getState();
  if (entry.channelId) {
    store.setChannelSeq(entry.organizationId, entry.channelId, entry.entityType, entry.untilSeq);
  } else {
    store.setOrgSeq(entry.organizationId, entry.entityType, entry.untilSeq);
  }
}

function requeue(entry: DirtyEntry, delayMs: number): void {
  const key = entryKey(entry.entityType, entry.organizationId, entry.channelId);
  const existing = dirty.get(key);
  // A newer change re-enqueued this channel view mid-flight: merge this range back into it.
  if (existing) mergeRange(existing, entry);
  else dirty.set(key, { ...entry, dueAt: Date.now() + delayMs });
  armTimer();
}

/** Widens a pending entry by another range of the same scope; the pending entry keeps its own due time. */
function mergeRange(into: DirtyEntry, from: Pick<DirtyEntry, 'afterSeq' | 'untilSeq' | 'hasCreates' | 'propagations'>): void {
  into.afterSeq = Math.min(into.afterSeq, from.afterSeq);
  into.untilSeq = Math.max(into.untilSeq, from.untilSeq);
  into.hasCreates ||= from.hasCreates;
  into.propagations.push(...from.propagations);
}

/** Flush pending ranges whose scope moved onto the live tier for catch-up on open. */
function promoteLiveChannels(): void {
  let changed = false;
  for (const entry of dirty.values()) {
    if (entry.dueAt > Date.now() && getSyncTier(entry.organizationId, entry.channelId).min === 0) {
      entry.dueAt = 0;
      changed = true;
    }
  }
  if (changed) void flushDue();
}

function installListeners(): void {
  if (listenersInstalled || typeof document === 'undefined') return;
  listenersInstalled = true;

  // Top up every scope before a hidden tab may go offline.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) void flushAllNow();
  });

  // Back online: retry what accumulated while offline.
  onlineManager.subscribe((online) => {
    if (online) armTimer();
  });

  // A mounting view puts its pending scope on the live tier; this also covers panels opened without navigation and changes that arrive just before mount.
  queryClient.getQueryCache().subscribe((event) => {
    if (event.type === 'observerAdded' && dirty.size > 0) promoteLiveChannels();
  });

  // Navigation flushes pending scopes and route context promotes org-level scopes; the lazy import keeps the route tree out of this module's importer graph.
  void import('~/routes/router').then(({ router }) => {
    router.subscribe('onLoad', promoteLiveChannels);
  });
}
